import React, { Fragment, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { AuthContext } from 'context/AuthProvider';
import { PopeyeContext } from 'context/PopeyeProvider';
import { LayoutContext } from 'context/LayoutProvider';
import Card from 'components/UI/Card/Card';
import Heading from 'components/UI/Heading/Heading';
import InstantBookingRibbon from 'components/UI/InstantBookingRibbon/InstantBookingRibbon';
import ReservationForm from './ReservationForm';
import PromotionsList from '../PromotionsList/PromotionsList';
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled';
import configForFE from 'common-pickapier/configForFE';

import { sanitizeTranslation } from 'library/helpers/translationHelpers';
import { getStateFromUrl } from 'library/helpers/url_handler';


import {
  NoReservationHeader,
  ReservationWidgetContainer,
} from './Reservation.style.js';
import Price from "./Price";
import PromotionEstimations from "./PromotionEstimations";

const getDateFromSearchParams = searchParams => {
  if (!isEmpty(searchParams) && searchParams.date_range) {
    return {
      setStartDate: searchParams.date_range.setStartDate
        ? moment.utc(searchParams.date_range.setStartDate, 'DD/MM/YYYY')
        : null,
      setEndDate: searchParams.date_range.setEndDate
        ? moment.utc(searchParams.date_range.setEndDate, 'DD/MM/YYYY')
        : null
    };
  } else {
    return {
      setStartDate: null,
      setEndDate: null
    };
  }
};

const CardHeader = ({
  priceStyle,
  marina,
  pricing,
  promotions,
  onOpenDatePicker
}) => {
  const { t } = useTranslation();

  const {
    popeyeLoyaltyStatus,
    isPreSignatureStatus,
    setVisibleRequest,
    isPopeyeMember
  } = useContext(PopeyeContext);

  const [state, dispatch] = useContext(LayoutContext);
  const { popeyeRequestDrawerOpened } = state;

  const { price, marina_settings } = marina;
  const { instant_booking } = marina_settings;

  if (!marina_settings.permissions.reservation) {
    return (
      <NoReservationHeader>
        <InfoCircleFilled style={{ color: '#194AB9' }} />
        {sanitizeTranslation(t('MARINA_DISABLED', {marina_name: marina.name}))}
      </NoReservationHeader>
    );
  }

  const handlePromotionDesktopClick = (startDate, endDate) => {
    if (isPopeyeMember || isPreSignatureStatus) {
      onOpenDatePicker(startDate, endDate);
    }
    if (configForFE.INCOMPLETE_POPEYE_STATUSES.includes(popeyeLoyaltyStatus))
      return setVisibleRequest(true);
  };

  const handlePromotionMobileClick = (startDate, endDate) => {
    if (isPopeyeMember || isPreSignatureStatus) {
      onOpenDatePicker(startDate, endDate);
    }

    if (configForFE.INCOMPLETE_POPEYE_STATUSES.includes(popeyeLoyaltyStatus))
      return dispatch({
        type: popeyeRequestDrawerOpened
          ? 'HIDE_POPEYE_REQUEST_DRAWER'
          : 'OPEN_POPEYE_REQUEST_DRAWER'
      });
  };

  return (
    <ReservationWidgetContainer>
      <PromotionsList
        promotions={promotions}
        handlePromotionMobileClick={handlePromotionMobileClick}
        handlePromotionDesktopClick={handlePromotionDesktopClick}
      />
      {instant_booking ? (
        <InstantBookingRibbon />
      ) : (
        <Heading
          content={
            <Fragment>
              <Price
                  price={price}
                  marina_settings={marina_settings}
                  pricing={pricing}
                  promotions={promotions}
              />
              <PromotionEstimations
                  pricing={pricing}
                  marina_settings={marina_settings}
              />
            </Fragment>
          }
          {...priceStyle}
        />
      )}
    </ReservationWidgetContainer>
  );
};

export default function Reservation({
  marina,
  isMessaging,
  location,
  history,
  promotions
}) {
  const { marina_settings } = marina;
  const { instant_booking } = marina_settings;
  const { user } = useContext(AuthContext);

  const searchParams = getStateFromUrl(location);
  const initialDate = getDateFromSearchParams(searchParams);

  const [pricing, setPricing] = useState({});
  const [focusedDateInput, setFocusedDateInput] = useState(null);
  const [date, setDate] = useState(initialDate);

  useEffect(() => {
    return () => setPricing({});
  }, [user]);

  const changePricing = pricing => {
    setPricing(pricing);
  };

  const onOpenDatePicker = (startDate, endDate) => {
    setFocusedDateInput('startDate');
    const todayFormattedMoment = moment().startOf('day');
    const startDayFormattedMoment = moment(startDate).startOf('day');
    const endDayFormattedMoment = moment(endDate).startOf('day');
    const endDayPlusOneFormattedMoment = moment(endDate).add(1, 'days').startOf('day');

    const isStartDatePassed = moment(startDate).isBefore(moment(), 'days');
    const setStartDate = isStartDatePassed
      ? todayFormattedMoment
      : startDayFormattedMoment;
    const isSameStartAndEnd = moment(setStartDate).isSame(
      endDayFormattedMoment,
      'day'
    );
    const setEndDate = isSameStartAndEnd
      ? endDayPlusOneFormattedMoment
      : endDayFormattedMoment;

    setDate({
      setStartDate,
      setEndDate
    });
  };

  return (
    <Card
      className={
        instant_booking
          ? 'reservation_sidebar instant_booking'
          : 'reservation_sidebar'
      }
      header={
        <CardHeader
          marina={marina}
          pricing={pricing}
          promotions={promotions}
          onOpenDatePicker={onOpenDatePicker}
        />
      }
      content={
        <ReservationForm
          location={location}
          history={history}
          marina={marina}
          isMessaging={isMessaging}
          changePricing={changePricing}
          focusedDateInput={focusedDateInput}
          setFocusedDateInput={setFocusedDateInput}
          date={date}
          setDate={setDate}
          getDateFromSearchParams={getDateFromSearchParams}
          promotions={promotions}
        />
      }
    />
  );
}

CardHeader.propTypes = {
  priceStyle: PropTypes.object,
  pricePeriodStyle: PropTypes.object,
  linkStyle: PropTypes.object
};

CardHeader.defaultProps = {
  priceStyle: {
    color: '#2C2C2C',
    fontSize: '25px',
    fontWeight: '700'
  },
  pricePeriodStyle: {
    fontSize: '15px',
    fontWeight: '400'
  },
  linkStyle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#194AB9'
  },
  disclaimerStyle: {
    marginBottom: 0,
    lineHeight: '20px'
  }
};
